import { firebase } from '../services/dataSvc';

/**
 * Initialize Firebase Analytics
 *
 * Configuration:
 * - Enabled in ALL environments (dev, test, prod, local) for easier testing
 * - Events tagged with environment name for filtering in Firebase console
 */
export function initAnalytics() {
  // Determine environment
  let environment = 'unknown';

  if (__DEV__) {
    environment = 'development';
  } else if (__TEST__) {
    environment = 'test';
  } else if (__PROD__) {
    environment = 'production';
  } else if (__LOCAL__) {
    environment = 'local';
  } else if (__LOCAL_LIVE__) {
    environment = 'local-live';
  }

  try {
    // Get analytics instance (automatically enabled with firebase/compat/analytics)
    const analytics = firebase.analytics();

    // Set user property for environment filtering
    analytics.setUserProperties({
      environment: environment
    });

    console.log(`[Analytics] Initialized for environment: ${environment}`);
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
  }
}

/**
 * Log a custom event to Firebase Analytics
 *
 * @param {string} eventName - Name of the event (e.g., 'user_login', 'post_create')
 * @param {object} eventParams - Event parameters/properties
 */
export function logEvent(eventName, eventParams = {}) {
  try {
    const analytics = firebase.analytics();

    // Add environment to all events for filtering
    const environment = getEnvironment();
    const paramsWithEnv = {
      ...eventParams,
      environment: environment
    };

    analytics.logEvent(eventName, paramsWithEnv);

    // Also log to console in non-production for debugging
    if (!__PROD__) {
      console.log(`[Analytics] Event: ${eventName}`, paramsWithEnv);
    }
  } catch (error) {
    console.error(`[Analytics] Failed to log event ${eventName}:`, error);
  }
}

/**
 * Log a page view to Firebase Analytics
 *
 * @param {string} page_path - The page path (e.g., '/board/123')
 * @param {string} page_title - Optional page title
 */
export function logPageView(page_path, page_title) {
  try {
    const analytics = firebase.analytics();
    analytics.logEvent('page_view', {
      page_path: page_path,
      page_title: page_title || document.title,
      environment: getEnvironment()
    });

    if (!__PROD__) {
      console.log(`[Analytics] Page view: ${page_path}`);
    }
  } catch (error) {
    console.error('[Analytics] Failed to log page view:', error);
  }
}

/**
 * Set the user ID for analytics tracking
 *
 * @param {string} userId - The user's ID
 */
export function setUserId(userId) {
  try {
    const analytics = firebase.analytics();
    analytics.setUserId(userId);

    if (!__PROD__) {
      console.log(`[Analytics] User ID set: ${userId}`);
    }
  } catch (error) {
    console.error('[Analytics] Failed to set user ID:', error);
  }
}

/**
 * Set user properties for analytics
 *
 * @param {object} properties - User properties object
 */
export function setUserProperties(properties) {
  try {
    const analytics = firebase.analytics();
    analytics.setUserProperties(properties);

    if (!__PROD__) {
      console.log('[Analytics] User properties set:', properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to set user properties:', error);
  }
}

/**
 * Get current environment name
 * @returns {string} Environment name
 */
function getEnvironment() {
  if (__DEV__) return 'development';
  if (__TEST__) return 'test';
  if (__PROD__) return 'production';
  if (__LOCAL__) return 'local';
  if (__LOCAL_LIVE__) return 'local-live';
  return 'unknown';
}

/**
 * Test function for Firebase Analytics
 * Can be called from browser console: window.testAnalytics()
 */
export function testAnalytics() {
  console.log('[Analytics] Running test...');

  try {
    // Test event logging
    logEvent('test_event', {
      test_parameter: 'test_value',
      timestamp: new Date().toISOString()
    });

    // Test page view
    logPageView('/test', 'Test Page');

    console.log('[Analytics] Test events sent! Check your Firebase console in a few minutes.');
    alert('Test analytics events sent! Check your Firebase console (Events tab) in a few minutes.');
  } catch (error) {
    console.error('[Analytics] Test failed:', error);
    alert('Test failed! Check console for errors.');
  }
}

// Export firebase analytics for advanced usage
export { firebase };
